import React, { useState, useEffect, useCallback } from 'react';
import { structureAPI, teamDirectoryAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Badge } from '../../components/ui/badge';
import { 
  ZoomIn, ZoomOut, Maximize2, User, Globe, Building2, 
  MapPin, Sparkles, Mail, Phone, Twitter, Linkedin, 
  Instagram, Briefcase, Users, ChevronRight, Target
} from 'lucide-react';

// Marketing Department Styling
const MARKETING_STYLES = {
  national: {
    cmo: { bg: '#7C3AED', text: '#FFFFFF', border: '#6D28D9' },
    director: { bg: '#8B5CF6', text: '#FFFFFF', border: '#7C3AED' },
    manager: { bg: '#A78BFA', text: '#FFFFFF', border: '#8B5CF6' }
  },
  regional: {
    manager: { bg: '#2A9D8F', text: '#FFFFFF', border: '#1E8A7D' },
    lead: { bg: '#48C9B0', text: '#1F2937', border: '#2A9D8F' },
    staff: { bg: '#A7F3D0', text: '#1F2937', border: '#48C9B0' }
  },
  uniqueIP: {
    director: { bg: '#F59E0B', text: '#FFFFFF', border: '#D97706' },
    head: { bg: '#FBBF24', text: '#1F2937', border: '#F59E0B' },
    lead: { bg: '#FCD34D', text: '#1F2937', border: '#FBBF24' },
    staff: { bg: '#FEF3C7', text: '#1F2937', border: '#FCD34D' }
  },
  vacant: { bg: '#FEE2E2', text: '#991B1B', border: '#FECACA' }
};

// Generate marketing roles from structure data
const generateMarketingHierarchy = (roles, people, teamMembers) => {
  // Helper to check if role is marketing-related
  const isMarketingRole = (role) => {
    const title = (role.title || '').toLowerCase();
    // Handle department as either string or object
    const deptValue = role.department;
    const dept = (typeof deptValue === 'string' ? deptValue : deptValue?.name || role.departmentName || '').toLowerCase();
    const skills = role.skills || [];
    
    return (
      dept.includes('marketing') || 
      dept.includes('media') ||
      title.includes('marketing') ||
      title.includes('media') ||
      title.includes('brand') ||
      title.includes('communications') ||
      title.includes('pr ') ||
      title.includes('public relations') ||
      skills.some(s => (s || '').toLowerCase().includes('marketing'))
    );
  };

  // Filter all marketing roles
  const marketingRoles = roles.filter(isMarketingRole);
  
  // Also check team directory for marketing members
  const marketingTeamMembers = (teamMembers || []).filter(member => {
    const dept = (member.department || '').toLowerCase();
    const title = (member.title || '').toLowerCase();
    const roles = member.roles || [];
    
    return (
      dept.includes('marketing') ||
      title.includes('marketing') ||
      roles.some(r => (r.department || '').toLowerCase().includes('marketing'))
    );
  });

  // Build hierarchy structure
  const hierarchy = {
    national: {
      cmo: null,
      director: null,
      regions: {}
    },
    uniqueIP: {
      director: null,
      heads: [],
      leads: [],
      staff: []
    }
  };

  // Populate National tier from roles
  marketingRoles.forEach(role => {
    const scopeType = role.scopeType || 'national';
    const title = (role.title || '').toLowerCase();
    const level = (role.levelLabel || '').toLowerCase();

    if (scopeType === 'national' || scopeType === 'company') {
      if (title.includes('chief marketing') || title.includes('cmo')) {
        hierarchy.national.cmo = role;
      } else if (title.includes('director') || level === 'director') {
        hierarchy.national.director = role;
      }
    } else if (scopeType === 'unique_ip') {
      if (title.includes('director')) {
        hierarchy.uniqueIP.director = role;
      } else if (title.includes('head')) {
        hierarchy.uniqueIP.heads.push(role);
      } else if (title.includes('lead')) {
        hierarchy.uniqueIP.leads.push(role);
      } else {
        hierarchy.uniqueIP.staff.push(role);
      }
    } else if (scopeType?.startsWith('region')) {
      const regionNum = scopeType.replace('region_', '').replace('regional_', '');
      if (!hierarchy.national.regions[regionNum]) {
        hierarchy.national.regions[regionNum] = { manager: null, leads: [], staff: [] };
      }
      
      if (title.includes('manager') || level === 'manager') {
        hierarchy.national.regions[regionNum].manager = role;
      } else if (title.includes('lead') || level === 'lead') {
        hierarchy.national.regions[regionNum].leads.push(role);
      } else {
        hierarchy.national.regions[regionNum].staff.push(role);
      }
    }
  });

  // Add team members to regions if not covered by roles
  marketingTeamMembers.forEach(member => {
    const region = member.region || '';
    const title = (member.title || '').toLowerCase();
    const regionMatch = region.match(/region[_\s]*(\d)/i);
    
    if (regionMatch) {
      const regionNum = regionMatch[1];
      if (!hierarchy.national.regions[regionNum]) {
        hierarchy.national.regions[regionNum] = { manager: null, leads: [], staff: [] };
      }
      
      // Create a role-like object from team member
      const memberRole = {
        id: member._id || member.id,
        title: member.title || 'Marketing Team Member',
        assignedPersonId: member._id || member.id,
        assignedPerson: {
          displayName: member.displayName || `${member.firstName} ${member.lastName}`,
          email: member.email,
          phone: member.phone,
          socialMedia: member.socialMedia
        },
        scopeType: `region_${regionNum}`,
        department: 'Marketing & Media',
        skills: member.skills
      };

      if (title.includes('lead')) {
        hierarchy.national.regions[regionNum].leads.push(memberRole);
      } else if (!title.includes('manager') && !title.includes('director')) {
        hierarchy.national.regions[regionNum].staff.push(memberRole);
      }
    }
  });

  return hierarchy;
};

// Demo marketing hierarchy data
const DEMO_MARKETING_HIERARCHY = {
  national: {
    cmo: {
      id: 'cmo-1',
      title: 'Chief Marketing Officer',
      assignedPersonId: 'p-cmo',
      assignedPerson: { displayName: 'Victoria Hayes', email: 'victoria.hayes@cognitive.com' },
      scopeType: 'national',
      levelLabel: 'C-Suite'
    },
    director: {
      id: 'dir-1',
      title: 'National Marketing Director',
      assignedPersonId: 'p-dir',
      assignedPerson: { displayName: 'Marcus Chen', email: 'marcus.chen@cognitive.com' },
      scopeType: 'national',
      levelLabel: 'Director'
    },
    regions: {
      '1': {
        manager: {
          id: 'rm-1',
          title: 'Region 1 Marketing Manager',
          assignedPersonId: 'p-rm1',
          assignedPerson: { displayName: 'Sarah Johnson', email: 'sarah.johnson@cognitive.com' },
          scopeType: 'region_1',
          levelLabel: 'Manager'
        },
        leads: [
          { id: 'rl-1-1', title: 'Brand Lead', assignedPersonId: 'p-rl11', assignedPerson: { displayName: 'James Wilson' }, scopeType: 'region_1' },
          { id: 'rl-1-2', title: 'Digital Lead', assignedPersonId: 'p-rl12', assignedPerson: { displayName: 'Emma Davis' }, scopeType: 'region_1' }
        ],
        staff: [
          { id: 'rs-1-1', title: 'Content Specialist', assignedPersonId: 'p-rs11', assignedPerson: { displayName: 'Alex Brown' }, scopeType: 'region_1' },
          { id: 'rs-1-2', title: 'Social Media Coordinator', assignedPersonId: 'p-rs12', assignedPerson: { displayName: 'Lisa Park' }, scopeType: 'region_1' }
        ]
      },
      '2': {
        manager: {
          id: 'rm-2',
          title: 'Region 2 Marketing Manager',
          assignedPersonId: 'p-rm2',
          assignedPerson: { displayName: 'David Kim', email: 'david.kim@cognitive.com' },
          scopeType: 'region_2',
          levelLabel: 'Manager'
        },
        leads: [
          { id: 'rl-2-1', title: 'Events Lead', assignedPerson: null, scopeType: 'region_2' },
          { id: 'rl-2-2', title: 'PR Lead', assignedPersonId: 'p-rl22', assignedPerson: { displayName: 'Rachel Green' }, scopeType: 'region_2' }
        ],
        staff: [
          { id: 'rs-2-1', title: 'Graphic Designer', assignedPersonId: 'p-rs21', assignedPerson: { displayName: 'Mike Chen' }, scopeType: 'region_2' },
          { id: 'rs-2-2', title: 'Marketing Analyst', assignedPersonId: 'p-rs22', assignedPerson: { displayName: 'Nina Patel' }, scopeType: 'region_2' }
        ]
      },
      '3': {
        manager: {
          id: 'rm-3',
          title: 'Region 3 Marketing Manager',
          assignedPerson: null,
          scopeType: 'region_3',
          levelLabel: 'Manager'
        },
        leads: [
          { id: 'rl-3-1', title: 'Content Lead', assignedPersonId: 'p-rl31', assignedPerson: { displayName: 'Tom Harris' }, scopeType: 'region_3' }
        ],
        staff: [
          { id: 'rs-3-1', title: 'Marketing Associate', assignedPersonId: 'p-rs31', assignedPerson: { displayName: 'Sara White' }, scopeType: 'region_3' }
        ]
      },
      '4': {
        manager: {
          id: 'rm-4',
          title: 'Region 4 Marketing Manager',
          assignedPersonId: 'p-rm4',
          assignedPerson: { displayName: 'Jennifer Lee', email: 'jennifer.lee@cognitive.com' },
          scopeType: 'region_4',
          levelLabel: 'Manager'
        },
        leads: [
          { id: 'rl-4-1', title: 'Digital Marketing Lead', assignedPersonId: 'p-rl41', assignedPerson: { displayName: 'Chris Evans' }, scopeType: 'region_4' },
          { id: 'rl-4-2', title: 'Campaign Lead', assignedPersonId: 'p-rl42', assignedPerson: { displayName: 'Amy Zhang' }, scopeType: 'region_4' }
        ],
        staff: [
          { id: 'rs-4-1', title: 'SEO Specialist', assignedPersonId: 'p-rs41', assignedPerson: { displayName: 'Ryan Murphy' }, scopeType: 'region_4' },
          { id: 'rs-4-2', title: 'Email Marketing', assignedPersonId: 'p-rs42', assignedPerson: { displayName: 'Kelly Thompson' }, scopeType: 'region_4' }
        ]
      },
      '5': {
        manager: {
          id: 'rm-5',
          title: 'Region 5 Marketing Manager',
          assignedPersonId: 'p-rm5',
          assignedPerson: { displayName: 'Robert Garcia', email: 'robert.garcia@cognitive.com' },
          scopeType: 'region_5',
          levelLabel: 'Manager'
        },
        leads: [
          { id: 'rl-5-1', title: 'Brand Lead', assignedPersonId: 'p-rl51', assignedPerson: { displayName: 'Michelle Wong' }, scopeType: 'region_5' }
        ],
        staff: [
          { id: 'rs-5-1', title: 'Creative Designer', assignedPersonId: 'p-rs51', assignedPerson: { displayName: 'Daniel Brown' }, scopeType: 'region_5' },
          { id: 'rs-5-2', title: 'Content Writer', assignedPersonId: 'p-rs52', assignedPerson: { displayName: 'Emily Chen' }, scopeType: 'region_5' }
        ]
      }
    }
  },
  uniqueIP: {
    director: {
      id: 'ip-dir',
      title: 'Director of Marketing & Media',
      assignedPersonId: 'p-ip-dir',
      assignedPerson: { displayName: 'Angela Foster', email: 'angela.foster@soulfood.com' },
      scopeType: 'unique_ip',
      levelLabel: 'Director'
    },
    heads: [
      { id: 'ip-h1', title: 'Head of Brand', assignedPersonId: 'p-ip-h1', assignedPerson: { displayName: 'Jordan Rivers' }, scopeType: 'unique_ip' },
      { id: 'ip-h2', title: 'Head of Digital', assignedPersonId: 'p-ip-h2', assignedPerson: { displayName: 'Sophia Martinez' }, scopeType: 'unique_ip' }
    ],
    leads: [
      { id: 'ip-l1', title: 'Social Media Lead', assignedPersonId: 'p-ip-l1', assignedPerson: { displayName: 'Kevin Park' }, scopeType: 'unique_ip' },
      { id: 'ip-l2', title: 'Content Lead', assignedPersonId: 'p-ip-l2', assignedPerson: { displayName: 'Maria Santos' }, scopeType: 'unique_ip' },
      { id: 'ip-l3', title: 'PR Lead', assignedPerson: null, scopeType: 'unique_ip' }
    ],
    staff: [
      { id: 'ip-s1', title: 'Graphic Designer', assignedPersonId: 'p-ip-s1', assignedPerson: { displayName: 'Tyler Adams' }, scopeType: 'unique_ip' },
      { id: 'ip-s2', title: 'Video Producer', assignedPersonId: 'p-ip-s2', assignedPerson: { displayName: 'Jasmine Liu' }, scopeType: 'unique_ip' },
      { id: 'ip-s3', title: 'Community Manager', assignedPersonId: 'p-ip-s3', assignedPerson: { displayName: 'Brandon Cole' }, scopeType: 'unique_ip' },
      { id: 'ip-s4', title: 'Marketing Analyst', assignedPersonId: 'p-ip-s4', assignedPerson: { displayName: 'Priya Sharma' }, scopeType: 'unique_ip' }
    ]
  }
};

// Role Node Component
const RoleNode = ({ role, style, onClick, size = 'normal' }) => {
  const isVacant = !role?.assignedPersonId;
  const nodeStyle = isVacant ? MARKETING_STYLES.vacant : style;
  const width = size === 'small' ? 'w-[120px]' : size === 'large' ? 'w-[180px]' : 'w-[150px]';
  
  return (
    <div
      className={`flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg ${width}`}
      onClick={() => onClick?.(role)}
      data-testid={`marketing-node-${role?.id}`}
    >
      <div
        className="rounded-lg shadow-md overflow-hidden"
        style={{ backgroundColor: nodeStyle.bg, border: `2px solid ${nodeStyle.border}` }}
      >
        <div className="px-2 py-1.5 text-center">
          <h3 className="font-semibold text-[10px] leading-tight" style={{ color: nodeStyle.text }}>
            {role?.title || 'Role'}
          </h3>
        </div>
        <div 
          className="px-2 py-1 text-center"
          style={{ backgroundColor: isVacant ? '#FEF2F2' : 'rgba(255,255,255,0.95)' }}
        >
          {isVacant ? (
            <span className="text-[10px] font-medium" style={{ color: '#991B1B' }}>Vacant</span>
          ) : (
            <span className="text-[10px] font-medium" style={{ color: '#374151' }}>
              {role?.assignedPerson?.displayName || 'Assigned'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Tier Header Component
const TierHeader = ({ label, color = '#6B7280' }) => (
  <div className="flex justify-center mb-3">
    <div className="px-4 py-1.5 rounded-full shadow-sm" style={{ backgroundColor: color }}>
      <span className="text-xs font-semibold uppercase tracking-wide text-white">{label}</span>
    </div>
  </div>
);

// Connector Line Component
const ConnectorLine = ({ height = 'h-6' }) => (
  <div className="flex justify-center">
    <div className={`w-0.5 ${height}`} style={{ backgroundColor: '#CBD5E1' }} />
  </div>
);

// Person Profile Sheet
const PersonProfileSheet = ({ open, onClose, role }) => {
  if (!role) return null;
  
  const person = role.assignedPerson;
  const isVacant = !role.assignedPersonId;
  
  // Demo social media stats
  const socialStats = {
    twitter: { followers: '12.4K', handle: '@' + (person?.displayName?.split(' ')[0]?.toLowerCase() || 'user') },
    linkedin: { connections: '500+' },
    instagram: { followers: '8.2K' }
  };

  // Demo skills
  const skills = ['Marketing & Media', 'Brand Strategy', 'Digital Marketing', 'Campaign Management', 'Content Strategy'];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:max-w-[400px] overflow-y-auto">
        <SheetHeader className="pb-4 border-b" style={{ borderColor: '#E9ECEF' }}>
          <SheetTitle className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
            >
              {isVacant ? (
                <User className="w-6 h-6" />
              ) : (
                <span className="text-lg font-semibold">
                  {person?.displayName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#264653' }}>
                {isVacant ? 'Vacant Position' : person?.displayName}
              </h2>
              <p className="text-sm font-normal" style={{ color: '#6B7280' }}>{role.title}</p>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="py-5 space-y-5">
          {/* Scope Badge */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Scope</h4>
            <Badge 
              variant="outline" 
              className="px-3 py-1"
              style={{ 
                borderColor: role.scopeType === 'unique_ip' ? '#F59E0B' : role.scopeType?.includes('region') ? '#2A9D8F' : '#7C3AED',
                color: role.scopeType === 'unique_ip' ? '#D97706' : role.scopeType?.includes('region') ? '#1E8A7D' : '#6D28D9'
              }}
            >
              <Globe className="w-3 h-3 mr-1.5" />
              {role.scopeType === 'unique_ip' ? 'UNIQUE IP' : 
               role.scopeType?.includes('region') ? `Regional - ${role.scopeType.replace('region_', 'Region ')}` : 
               'National'}
            </Badge>
          </div>

          {/* Contact Info */}
          {!isVacant && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Contact</h4>
              <div className="space-y-2">
                {person?.email && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: '#374151' }}>
                    <Mail className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span>{person.email}</span>
                  </div>
                )}
                {person?.phone && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: '#374151' }}>
                    <Phone className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span>{person.phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Other Roles Held */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Other Roles Held</h4>
            <div className="space-y-2">
              {isVacant ? (
                <p className="text-sm" style={{ color: '#9CA3AF' }}>No person assigned</p>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: '#F3F4F6' }}>
                    <Briefcase className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span className="text-sm" style={{ color: '#374151' }}>Marketing Committee Member</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: '#F3F4F6' }}>
                    <Users className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span className="text-sm" style={{ color: '#374151' }}>Brand Council Advisor</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Social Media Stats */}
          {!isVacant && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Social Media</h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#E0F2FE' }}>
                  <Twitter className="w-4 h-4 mx-auto mb-1" style={{ color: '#0EA5E9' }} />
                  <p className="text-xs font-semibold" style={{ color: '#0369A1' }}>{socialStats.twitter.followers}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>Followers</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#EDE9FE' }}>
                  <Linkedin className="w-4 h-4 mx-auto mb-1" style={{ color: '#7C3AED' }} />
                  <p className="text-xs font-semibold" style={{ color: '#5B21B6' }}>{socialStats.linkedin.connections}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>Connections</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#FCE7F3' }}>
                  <Instagram className="w-4 h-4 mx-auto mb-1" style={{ color: '#EC4899' }} />
                  <p className="text-xs font-semibold" style={{ color: '#BE185D' }}>{socialStats.instagram.followers}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>Followers</p>
                </div>
              </div>
            </div>
          )}

          {/* Skills */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Skills</h4>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill, idx) => (
                <Badge 
                  key={idx}
                  variant="secondary"
                  className="text-xs"
                  style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
                >
                  {skill}
                </Badge>
              ))}
            </div>
          </div>

          {/* Level Info */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E9ECEF' }}>
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: '#6B7280' }}>Level</span>
              <span className="text-sm font-medium" style={{ color: '#264653' }}>
                {role.levelLabel || 'Staff'}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs" style={{ color: '#6B7280' }}>Department</span>
              <span className="text-sm font-medium" style={{ color: '#264653' }}>Marketing & Media</span>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Main Marketing Department View Component
const MarketingDepartmentView = () => {
  const [hierarchy, setHierarchy] = useState(DEMO_MARKETING_HIERARCHY);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [zoom, setZoom] = useState(0.85);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [rolesRes, peopleRes, teamRes] = await Promise.all([
          structureAPI.getRoles({ status: 'all' }).catch(() => ({ data: { roles: [] } })),
          structureAPI.getPeople().catch(() => ({ data: { people: [] } })),
          teamDirectoryAPI.getAll().catch(() => ({ data: { members: [] } }))
        ]);
        
        const computedHierarchy = generateMarketingHierarchy(
          rolesRes.data.roles || [],
          peopleRes.data.people || [],
          teamRes.data.members || []
        );
        
        // Merge with demo data for complete view
        // Use demo data as fallback if computed data is incomplete
        if (!computedHierarchy.national.cmo) {
          computedHierarchy.national.cmo = DEMO_MARKETING_HIERARCHY.national.cmo;
        }
        if (!computedHierarchy.national.director) {
          computedHierarchy.national.director = DEMO_MARKETING_HIERARCHY.national.director;
        }
        
        // Ensure all 5 regions exist
        for (let i = 1; i <= 5; i++) {
          if (!computedHierarchy.national.regions[i]) {
            computedHierarchy.national.regions[i] = DEMO_MARKETING_HIERARCHY.national.regions[i];
          }
        }
        
        // Use demo UNIQUE IP if not populated
        if (!computedHierarchy.uniqueIP.director) {
          computedHierarchy.uniqueIP = DEMO_MARKETING_HIERARCHY.uniqueIP;
        }
        
        setHierarchy(computedHierarchy);
      } catch (error) {
        console.error('Error fetching marketing data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleNodeClick = (role) => {
    setSelectedRole(role);
    setProfileOpen(true);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 1.3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));
  const handleResetZoom = () => setZoom(0.85);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[700px] rounded-xl" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: '#7C3AED', borderTopColor: 'transparent' }} />
          <p style={{ color: '#6B7280' }}>Loading marketing department view...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className="rounded-xl overflow-hidden relative" 
        style={{ height: '800px', backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB' }}
        data-testid="marketing-department-view"
      >
        {/* Header */}
        <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
          {/* Zoom Controls */}
          <div className="flex gap-2 bg-white rounded-lg shadow-md p-2">
            <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-8 w-8 p-0" title="Zoom In">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-8 w-8 p-0" title="Zoom Out">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleResetZoom} className="h-8 w-8 p-0" title="Reset Zoom">
              <Maximize2 className="w-4 h-4" />
            </Button>
            <div className="text-xs flex items-center px-2" style={{ color: '#6B7280' }}>{Math.round(zoom * 100)}%</div>
          </div>

          {/* Title */}
          <div className="bg-white rounded-lg shadow-md px-4 py-2">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5" style={{ color: '#7C3AED' }} />
              <span className="font-semibold" style={{ color: '#264653' }}>Marketing Department View</span>
              <Badge variant="outline" className="text-xs" style={{ borderColor: '#7C3AED', color: '#7C3AED' }}>
                Functional Matrix
              </Badge>
            </div>
          </div>

          {/* Legend */}
          <div className="bg-white rounded-lg shadow-md p-3">
            <div className="text-xs font-medium mb-2" style={{ color: '#374151' }}>Legend</div>
            <div className="flex gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#7C3AED' }} />
                <span className="text-[10px]" style={{ color: '#6B7280' }}>National</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#2A9D8F' }} />
                <span className="text-[10px]" style={{ color: '#6B7280' }}>Regional</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F59E0B' }} />
                <span className="text-[10px]" style={{ color: '#6B7280' }}>UNIQUE IP</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA' }} />
                <span className="text-[10px]" style={{ color: '#6B7280' }}>Vacant</span>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Content */}
        <ScrollArea className="h-full w-full pt-20">
          <div 
            className="min-h-full py-8 px-4"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', minWidth: 'max-content' }}
          >
            {/* =============== NATIONAL TIER =============== */}
            <div className="mb-12">
              {/* TIER 1: CMO */}
              <TierHeader label="Chief Marketing Officer" color="#7C3AED" />
              <div className="flex justify-center mb-4">
                <RoleNode 
                  role={hierarchy.national.cmo} 
                  style={MARKETING_STYLES.national.cmo}
                  onClick={handleNodeClick}
                  size="large"
                />
              </div>
              <ConnectorLine />

              {/* TIER 2: National Director */}
              <TierHeader label="National Marketing Director" color="#8B5CF6" />
              <div className="flex justify-center mb-4">
                <RoleNode 
                  role={hierarchy.national.director} 
                  style={MARKETING_STYLES.national.director}
                  onClick={handleNodeClick}
                  size="large"
                />
              </div>
              <ConnectorLine />

              {/* TIER 3: Regional Marketing Managers */}
              <TierHeader label="Regional Marketing Managers" color="#2A9D8F" />
              <div className="flex justify-center gap-6 mb-4">
                {[1, 2, 3, 4, 5].map(regionNum => (
                  <div key={regionNum} className="flex flex-col items-center">
                    <div className="text-[10px] font-medium mb-2 px-2 py-0.5 rounded" style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}>
                      Region {regionNum}
                    </div>
                    <RoleNode 
                      role={hierarchy.national.regions[regionNum]?.manager} 
                      style={MARKETING_STYLES.regional.manager}
                      onClick={handleNodeClick}
                    />
                  </div>
                ))}
              </div>
              <ConnectorLine height="h-4" />

              {/* TIER 4: Regional Leads */}
              <TierHeader label="Regional Marketing Leads" color="#48C9B0" />
              <div className="flex justify-center gap-6 mb-4">
                {[1, 2, 3, 4, 5].map(regionNum => (
                  <div key={regionNum} className="flex flex-col items-center gap-2 min-w-[150px]">
                    {hierarchy.national.regions[regionNum]?.leads?.map((lead, idx) => (
                      <RoleNode 
                        key={lead.id || idx}
                        role={lead} 
                        style={MARKETING_STYLES.regional.lead}
                        onClick={handleNodeClick}
                        size="small"
                      />
                    ))}
                    {(!hierarchy.national.regions[regionNum]?.leads?.length) && (
                      <div className="text-[10px] py-2" style={{ color: '#9CA3AF' }}>No leads</div>
                    )}
                  </div>
                ))}
              </div>
              <ConnectorLine height="h-4" />

              {/* TIER 5: Regional Staff */}
              <TierHeader label="Regional Marketing Staff" color="#A7F3D0" />
              <div className="flex justify-center gap-6 mb-4">
                {[1, 2, 3, 4, 5].map(regionNum => (
                  <div key={regionNum} className="flex flex-col items-center gap-2 min-w-[150px]">
                    {hierarchy.national.regions[regionNum]?.staff?.map((staff, idx) => (
                      <RoleNode 
                        key={staff.id || idx}
                        role={staff} 
                        style={MARKETING_STYLES.regional.staff}
                        onClick={handleNodeClick}
                        size="small"
                      />
                    ))}
                    {(!hierarchy.national.regions[regionNum]?.staff?.length) && (
                      <div className="text-[10px] py-2" style={{ color: '#9CA3AF' }}>No staff</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* =============== UNIQUE IP TIER =============== */}
            <div 
              className="mt-8 pt-8 border-t-2"
              style={{ borderColor: '#F59E0B', borderStyle: 'dashed' }}
            >
              <div className="flex justify-center mb-4">
                <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#FEF3C7', border: '2px solid #F59E0B' }}>
                  <span className="font-bold text-sm uppercase tracking-wider" style={{ color: '#D97706' }}>
                    UNIQUE IP Marketing Branch
                  </span>
                </div>
              </div>

              {/* UNIQUE IP Director */}
              <TierHeader label="Director of Marketing & Media" color="#F59E0B" />
              <div className="flex justify-center mb-4">
                <RoleNode 
                  role={hierarchy.uniqueIP.director} 
                  style={MARKETING_STYLES.uniqueIP.director}
                  onClick={handleNodeClick}
                  size="large"
                />
              </div>
              <ConnectorLine />

              {/* UNIQUE IP Heads */}
              <TierHeader label="Department Heads" color="#FBBF24" />
              <div className="flex justify-center gap-4 mb-4">
                {hierarchy.uniqueIP.heads.map((head, idx) => (
                  <RoleNode 
                    key={head.id || idx}
                    role={head} 
                    style={MARKETING_STYLES.uniqueIP.head}
                    onClick={handleNodeClick}
                  />
                ))}
              </div>
              <ConnectorLine />

              {/* UNIQUE IP Leads */}
              <TierHeader label="Team Leads" color="#FCD34D" />
              <div className="flex justify-center gap-3 mb-4 flex-wrap">
                {hierarchy.uniqueIP.leads.map((lead, idx) => (
                  <RoleNode 
                    key={lead.id || idx}
                    role={lead} 
                    style={MARKETING_STYLES.uniqueIP.lead}
                    onClick={handleNodeClick}
                    size="small"
                  />
                ))}
              </div>
              <ConnectorLine />

              {/* UNIQUE IP Staff */}
              <TierHeader label="Team Members" color="#FEF3C7" />
              <div className="flex justify-center gap-3 flex-wrap">
                {hierarchy.uniqueIP.staff.map((staff, idx) => (
                  <RoleNode 
                    key={staff.id || idx}
                    role={staff} 
                    style={MARKETING_STYLES.uniqueIP.staff}
                    onClick={handleNodeClick}
                    size="small"
                  />
                ))}
              </div>
            </div>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Person Profile Sheet */}
      <PersonProfileSheet 
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        role={selectedRole}
      />
    </>
  );
};

export default MarketingDepartmentView;
